import { useState } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../../../components/Modal/CustomModal";
import DefaultImage from "../../../../../assets/IconsButtons/ic-ingredient-card.svg";
import ButtonAction from "../../../../../components/buttons/ButtonAction";
import renderAllergenIcons from "../../../Dining/Dishes/SubComponents/AllergenIcons";

/**
 * Modal component to display detailed information about a dish,
 * including image, description, allergens, and ingredients.
 *
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * @param {Object} props - Component props.
 * @param {Function} props.handleClose - Callback function to close the modal.
 * @param {boolean} props.showModal - Whether the modal is currently visible.
 * @param {Object} props.dish - Dish object containing its details.
 * @param {Object} [props.dish.foodieItem] - Detailed information about the dish.
 * @param {string} [props.dish.foodieItem.name] - Name of the dish.
 * @param {string} [props.dish.foodieItem.description] - Description of the dish.
 * @param {string} [props.dish.foodieItem.image] - Base64 encoded image of the dish.
 * @param {Array<{ ingredientName: string }>} [props.dish.foodieItem.ingredients] - List of ingredients for the dish.
 *
 * @returns {JSX.Element} `DishCardModal` component.
 */
const DishCardModal = ({ handleClose, showModal, dish }) => {
    const { t } = useTranslation();
    const infoDish = dish?.foodieItem;
    const [isOverview, setIsOverview] = useState(true);

    return (
        <div>
            <CustomModal
                show={showModal}
                onHide={handleClose}
                confirmButton={false}
                cancelButton={false}
                classNameBody="pt-2"
            >
                <div className="container-fluid px-0 container-dish-card-modal position-relative min-vh-35 overflow-auto">
                    <div className="row g-3">
                        <div className="col-12 col-md-6 order-1 order-md-1 d-flex justify-content-center align-items-center" >
                            <div className="meal-container w-250px-h-250px rounded-2 overflow-hidden w-100 position-static overflow-hidden">
                                <img
                                    src={
                                        infoDish?.image
                                            ? `data:image/png;base64,${infoDish?.image}`
                                            : DefaultImage
                                    }
                                    alt="meal"
                                    className="w-100 h-100 object-fit-cover"
                                />
                            </div>
                        </div>

                        {/* Texto */}
                        <div className="col-12 col-md-6 order-2 order-md-2 px-4">
                            <p className="fs-modal-title ibl-font-w-600">{infoDish?.name}</p>

                            <div className="d-flex gap-4 my-4 flex-wrap">
                                <ButtonAction
                                    id="btn_dcm_overview"
                                    name="overview"
                                    className={
                                        isOverview
                                            ? "btn-outlined-secondary-600-100"
                                            : "btn-outlined-secondary-600-00"
                                    }
                                    onClick={() => setIsOverview(true)}
                                />
                                <ButtonAction
                                    id="btn_dcm_ingredients"
                                    name="ingredients"
                                    className={
                                        !isOverview
                                            ? "btn-outlined-secondary-600-100"
                                            : "btn-outlined-secondary-600-00"
                                    }
                                    onClick={() => setIsOverview(false)}
                                />
                            </div>

                            <div className="border-bottom-shades-300 py-2"/>

                            {isOverview ? (
                                <div className="pt-2">
                                    <p className="ibl-paragraph-black fs-14px py-2">
                                        {t("description")}
                                    </p>
                                    <p className="ibl-font-w-400">{infoDish?.description}</p>
                                    <div className="d-flex flex-wrap gap-3 pt-4">
                                        {renderAllergenIcons(infoDish, t, true)}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <p className="ibl-paragraph-black fs-14px pt-2">
                                        {t("ingredients")}
                                    </p>
                                    <ul className="mb-0 ps-3">
                                        {infoDish?.ingredients?.map((ingredient, idx) => (
                                            <li
                                                key={idx}
                                                className="font-color-general-950 fs-14px ibl-font-w-400 pt-2"
                                            >
                                                {ingredient.ingredientName}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

export default DishCardModal;
